import { Currency } from '@sequencehq/api'
import { logError } from '@sequencehq/utils'
import { useQuery } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useState } from 'react'
import { useNotifications } from '@sequencehq/core-components'

type EnabledCurrenciesResponse =
  | {
      status: 'SUCCESS'
      enabledCurrencies: Currency[]
    }
  | {
      status: 'ERROR'
    }
  | {
      status: 'LOADING'
    }

type UseCurrenciesArgs = {
  toastOnError?: boolean
}

export const useEnabledCurrencies = ({
  toastOnError
}: UseCurrenciesArgs = {}): EnabledCurrenciesResponse => {
  const { displayNotification, hideNotification } = useNotifications()
  const [activeErrorToast, setActiveErrorToast] = useState<
    string | number | null
  >(null)
  // Possible next steps
  // - Enable currency sorting / prioritization

  const enabledCurrenciesRes = useQuery(
    dashboard20240730Client.getSequenceAccountSettings,
    undefined,
    {
      select: res => res?.enabledCurrencies
    }
  )

  if (enabledCurrenciesRes.isLoading) {
    if (activeErrorToast) {
      hideNotification(activeErrorToast)
      setActiveErrorToast(null)
    }

    return {
      status: 'LOADING'
    }
  }

  if (enabledCurrenciesRes.isSuccess) {
    const enabledCurrencies = enabledCurrenciesRes.data

    if (enabledCurrencies?.length) {
      return {
        status: 'SUCCESS',
        enabledCurrencies: [...new Set(enabledCurrencies)]
      }
    }
  }

  if (toastOnError && !activeErrorToast) {
    logError('Failed to load enabled currencies')
    const toastId = displayNotification(
      'Failed to load enabled currencies - Please refresh page to try again',
      {
        type: 'error'
      }
    )
    setActiveErrorToast(toastId)
  }

  return {
    status: 'ERROR'
  }
}

export const useDefaultCurrency = (enabled = true): Currency | undefined => {
  const { displayNotification } = useNotifications()
  // Possible next steps
  // - Enable currency sorting / prioritization

  const enabledCurrenciesRes = useQuery(
    dashboard20240730Client.getSequenceAccountSettings,
    undefined,
    {
      select: res => res?.enabledCurrencies,
      enabled
    }
  )

  if (enabledCurrenciesRes.isError) {
    displayNotification(
      'Failed to load default currency - Please refresh page to try again',
      {
        type: 'error'
      }
    )
  }

  return enabledCurrenciesRes.data?.[0]
}
